import { Component, OnInit, Input, Output, EventEmitter, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { FilterService } from 'src/app/core/filter/filter.service';
import { Filter, Scope } from 'src/app/core/filter/filter';
import { Router } from '@angular/router';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Organization } from 'src/app/core/model/organization';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { NgForm } from '@angular/forms';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  standalone: false,
  selector: 'app-save-filter',
  templateUrl: './save-filter.component.html',
  styleUrls: ['./save-filter.component.scss']
})
export class SaveFilterComponent implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('filterForm') public form: NgForm;

  @Input() public dialogOpened = false;
  @Input() public filterState: any = null;
  @Input() public filterColumns: any = null;
  @Input() public filterCategory: string;
  @Input() public filter: any;
  @Input() public showSystemFilter = false;
  @Input() public systemFilterEnabled = false;

  @Output() public cancelling = new EventEmitter();
  @Output() public save = new EventEmitter();

  public filterName: string = null;
  public filterSystem = false;
  public organization: Organization = null;
  public screen: any;
  public organizations = [];
  public isUpdate = false;
  public patientLabel = {};
  public patientLabelString: string;
  public filterScreen: Array<{ text: string; value: string; key: string }>;

  private coordinatorDashboardText: string;
  private patientSummaryText: string;
  private caseDetailsText: string;

  constructor(
    private filterService: FilterService,
    private router: Router,
    private organizationService: OrganizationService,
    private messageHandlerService: MessageHandlerService,
    private translateService: TranslateService,
    private settingService: SettingService,
    private authService: AuthenticationService,
    private messengerService: MessengerService) { }

  public get filterNameControl(): any
  {
    return (this.form !== undefined) ? this.form.form.controls.filterName : undefined;
  }

  public ngOnInit(): void
  {
    if (this.authService.isAuthenticated)
    {
      this.getOrganizations();
      this.settingService.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.patientLabelString = sessionSetting.patientLabel;
          this.translateService.get(['COORDINATOR-DASHBOARD', 'PATIENT-SUMMARY', 'CASE-DETAILS']).subscribe({
            next: (res) =>
            {
              this.coordinatorDashboardText = res['COORDINATOR-DASHBOARD'];
              this.patientSummaryText = res['PATIENT-SUMMARY'].replace('{{patientLabel}}', this.patientLabelString);
              this.caseDetailsText = res['CASE-DETAILS'];
            }
          });
        }
      });
    }
  }

  public ngOnChanges(): void
  {
    this.filterScreen = [
      { text: this.coordinatorDashboardText, value: '0', key: 'COORDINATOR_DASHBOARD' },
      {
        text: this.patientSummaryText, value: '1', key: 'PATIENT_SUMMARY'
      },
      { text: this.caseDetailsText, value: '2', key: 'CASE_DETAILS' },
    ];

    if (this.filter !== undefined && this.filter !== null)
    {
      this.filterName = this.filter.name;
      if (typeof this.filter.organization === 'number')
      {
        this.organization = this.organizations.find(o => o.id === this.filter.organization);
      }
      else
      {
        this.organization = this.filter.organization;
      }
      this.screen = this.filterScreen.find(fs => fs.key === this.filter.screen);
      this.isUpdate = true;
    }
    else
    {
      this.filterName = '';
      this.organization = null;
      this.isUpdate = false;
      this.screen = null;
    }
    this.filterSystem = this.showSystemFilter && this.filter && this.filter.category === 'CommunityServiceSystem';
  }

  public ngOnDestroy(): void
  {
    this.filterName = '';
    this.organization = null;
  }

  /**
   * Cancel the custom filter setting.
   */
  public cancelFilter(): void
  {
    // Reset filter name and organization
    this.filterName = null;

    this.organization = null;

    this.screen = null;
    // Close filter dialog
    this.cancelling.emit();
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Save the custom filter setting.
   */
  public saveFilter(): void
  {
    // Construct a filter object
    const filter: Filter = this.filterService.createFilter(JSON.stringify(this.filterState), this.filterName,
      this.filterCategory, (this.organization !== null) ? Scope.group : Scope.user,
      (this.organization !== null && this.organization !== undefined) ? this.organization.id : null,
      (this.filter !== undefined) ? this.filter.id : '',
      (this.screen !== null && this.screen !== undefined) ? this.screen.value : '');

    // Set filter column if used
    filter.columns = this.filterColumns ? JSON.stringify(this.filterColumns) : null;

    // Set proper filter category
    if (this.showSystemFilter && this.filterCategory === 'CommunityService')
    {
      filter.category = this.filterSystem ? 'CommunityServiceSystem' : 'CommunityService';
    }

    // Save filter to the server
    this.filterService.saveFilter(filter).subscribe({
      next: (r) =>
      {
        this.save.emit(r);

        // Immediately run this filter to update the page
        if (this.filterCategory !== 'CommunityService')
        {
          this.router.navigate([`${this.filterCategory.toLowerCase()}s/${r.id}`]);
          this.messengerService.sendMessage(new MessageSubject('filterUpdated', r));
        }
        this.messageHandlerService.displaySuccess('FILTER-UPDATED-SUCCESSFULLY');
        this.filter = undefined;
        this.cancelFilter();
      },
      error: (error: any) =>
      {
        // Conflict can occurs if duplicate filters are saved
        if (error && error.status === 409) 
        {
          this.messageHandlerService.displayWarning('FILTER-SELECTION-ALREADY-EXISTS');
        }
        else
        {
          this.messageHandlerService.displayError(error);
        }
      }
    });
  }

  private getOrganizations()
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
